import React, { useRef } from "react";
import { Helmet } from "react-helmet-async";
import Footer from "../../components/Footer";
import { useArticlesContext } from "../../context/ArticlesContext";
import MobileMapOverlay from "../../components/MobileMapOverlay";
import SocialMedia from "../../components/SocialMedia";
import Marker from "../../components/Marker";
import Top from "../../components-top/Top";
import Left from "../../components-top/Left";
import Right from "../../components-top/Right";
import Bottom from "../../components-top/Bottom";

export default function ArticleRentACarVancouver() {
  // 1) Provide a unique reference ID (the slug in your ArticlesContext)
  const ref_id = "rent-a-car-in-vancouver";

  // 2) Access articles from your context (optional usage)
  const { articles } = useArticlesContext();
  const ref_data = articles.find((art) => art.slug === ref_id);

  // =========================================================
  // ========== SECTION TITLES IDS (Labels Map) ==============
  // =========================================================
  const labelsMap = {
    intro: "Introduction",
    "types-of-rentals": "Types of Rentals",
    "where-to-rent": "Where to Rent",
    "local-regulations": "Local Regulations & Best Practices",
    faq: "FAQ",
    conclusion: "Conclusion",
    references: "References",
  };

  // ===================================
  // 4) References Data
  // ===================================
  const referencesData = [
    {
      url: "https://www.hellobc.com/",
      label: "Tourism British Columbia",
    },
    {
      url: "https://www.yvr.ca/en/passengers/transportation/car-rentals",
      label: "Vancouver International Airport - Car Rental Info",
    },
    {
      url: "https://www.icbc.com/vehicle-registration/insurance/Pages/Default.aspx",
      label: "ICBC - Insurance Info for BC Drivers",
    },
  ];

  // ===================================
  // 5) Locations for the Leaflet map
  // ===================================
  const locations = [
    {
      id: "airport-rental",
      lat: 49.19333,
      lng: -123.17566,
      popupText: "Vancouver Int'l Airport (YVR) Rental Center",
    },
    {
      id: "downtown-rental",
      lat: 49.28288,
      lng: -123.12046,
      popupText: "Downtown Vancouver Car Rental (Hornby St)",
    },
    {
      id: "cruise-terminal-rental",
      lat: 49.28880,
      lng: -123.11125,
      popupText: "Canada Place / Cruise Terminal Rental",
    },
    {
      id: "pacific-central-station",
      lat: 49.27361,
      lng: -123.09806,
      popupText: "Pacific Central Station (Train/Bus)",
    },
    {
      id: "north-vancouver-rental",
      lat: 49.31970,
      lng: -123.10300,
      popupText: "Enterprise North Vancouver - Marine Drive",
    },
  ];

  // Keep references to textual sections for scroll linking
  const spotRefs = useRef({});
  const setSpotRef = (id, el) => {
    spotRefs.current[id] = el;
  };

  // Desktop map reference
  const mapRef = useRef(null);

  // MOBILE MAP
  const mobileMapRef = useRef(null);

  return (
    <>
      {/* ============ SEO & METADATA ============ */}
      <Helmet>
        <title>Rent a Car in Vancouver: The Ultimate Guide</title>
        <meta
          name="description"
          content="Everything you need to know about renting a car in Vancouver—best options, local rules, and must-know tips."
        />
        <meta
          name="keywords"
          content="rent a car, Vancouver, car rental, driving tips"
        />
        <meta name="robots" content="index, follow" />
        <link
          rel="canonical"
          href="https://www.bcouver.com/articles/rent-a-car-in-vancouver.html"
        />

        {/* Open Graph / Facebook */}
        <meta
          property="og:title"
          content="Rent a Car in Vancouver: The Ultimate Guide"
        />
        <meta
          property="og:description"
          content="Your comprehensive resource for car rentals in Vancouver. Learn about top rental locations, local rules, and frequently asked questions."
        />
        <meta
          property="og:image"
          content="https://bcouver.com/assets/images/rent-a-car-in-vancouver.jpg"
        />
        <meta
          property="og:url"
          content="https://www.bcouver.com/articles/rent-a-car-in-vancouver.html"
        />
        <meta property="og:type" content="article" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Rent a Car in Vancouver: The Ultimate Guide"
        />
        <meta
          name="twitter:description"
          content="Your comprehensive resource for car rentals in Vancouver. Learn about top rental locations, local rules, and frequently asked questions."
        />
        <meta
          name="twitter:image"
          content="https://bcouver.com/assets/images/rent-a-car-in-vancouver.jpg"
        />
      </Helmet>

      {/* ============ TOP NAVBAR + HERO ============ */}
      <Top ref_id={ref_id} />

      <main>
        <div className="container article-main">
          {/* LEFT COLUMN (TableOfContents + Map) */}
          <Left
            labelsMap={labelsMap}
            locations={locations}
            mapRef={mapRef}
            spotRefs={spotRefs}
          />

          {/* ============ ARTICLE CONTENT ============ */}
          <article className="article-content">
            {/* Featured Image */}
            <img
              src={
                ref_data?.image ||
                "https://bcouver.com/assets/images/article-fallback.jpg"
              }
              alt="Scenic shot of Vancouver roads and skyline"
              className="featured-image"
            />

            {/* Social Share */}
            <SocialMedia />

            {/* SECTION: Introduction */}
            <h2 id="intro">Introduction</h2>
            <p>
              Vancouver’s breathtaking mountains, ocean views, and bustling
              neighborhoods offer a world of exploration. While downtown is
              walkable and well-connected by public transport, renting a car
              gives you the freedom to explore the wider region at your own
              pace—from Whistler’s slopes to ferry terminals leading to Vancouver
              Island. This guide covers everything you need to know about
              renting a car in Vancouver, including types of rentals, top pickup
              locations, local driving regulations, and answers to frequently
              asked questions.
            </p>

            {/* SECTION: Types of Rentals */}
            <h2 id="types-of-rentals">Types of Rentals</h2>
            <p>
              Vancouver has an array of rental options suitable for tourists,
              business travelers, or residents needing temporary wheels. Economy
              cars are ideal for budget travelers or solo adventurers, while
              standard and full-size vehicles provide extra comfort for families
              or groups. If you’re heading to the mountains, consider renting an
              SUV or minivan for more space. Meanwhile, those seeking luxury can
              opt for high-end or exotic cars, and eco-conscious travelers may
              find hybrid or fully electric models, reflecting Vancouver’s
              environmental focus.
            </p>
            <p>
              One-way rentals are also popular. For instance, you could pick up a
              car in Vancouver and drop it off elsewhere in British Columbia or
              even in another province. Just be sure to confirm any cross-border
              details if you plan to drive into the United States.
            </p>

            {/* SECTION: Where to Rent */}
            <h2 id="where-to-rent">Where to Rent</h2>
            <p>
              Whether you’re flying in, arriving by cruise ship, or staying in
              North Vancouver, there are convenient rental locations across the
              metro area.
            </p>

            <h3>1. Vancouver International Airport (YVR)</h3>
            <p
              ref={(el) => setSpotRef("airport-rental", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Vancouver Intl Airport Rental Center</strong>{" "}
              <Marker
                coordY={49.19333}
                coordX={-123.17566}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              – Located at&nbsp;
              <em>3211 Grant McConachie Way, Richmond, BC V7B 0A4</em>. Multiple
              major providers like Avis, Hertz, and Enterprise are on-site.
              Airport rentals may incur extra fees, but you can walk straight
              from the terminal to your vehicle. Rates often start around
              CA$30–$50/day for economy cars, though prices fluctuate with
              demand.
            </p>

            <h3>2. Downtown Vancouver – Hornby & West Georgia</h3>
            <p
              ref={(el) => setSpotRef("downtown-rental", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Downtown Vancouver Car Rental (Hornby St)</strong>{" "}
              <Marker
                coordY={49.28288}
                coordX={-123.12046}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              – A central location near hotels and offices, perfect for
              travelers staying downtown. Address: <em>757 Hornby St, Vancouver, BC</em>. 
              Here you’ll find competitive daily rates (around CA$40–$60)
              without the airport surcharge. Keep in mind downtown parking fees
              can be high, so consider renting only when you truly need a car.
            </p>

            <h3>3. Canada Place / Cruise Terminal</h3>
            <p
              ref={(el) => setSpotRef("cruise-terminal-rental", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Canada Place / Cruise Terminal Rental</strong>{" "}
              <Marker
                coordY={49.28880}
                coordX={-123.11125}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              – Near <em>999 Canada Place, Vancouver, BC</em>, this is a top
              choice for cruise passengers or convention attendees. You can
              conveniently pick up a car right after disembarking. Rates are on
              par with downtown prices, but book ahead to secure availability on
              busy cruise ship days.
            </p>

            <h3>4. Pacific Central Station</h3>
            <p
              ref={(el) => setSpotRef("pacific-central-station", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Pacific Central Station (Train/Bus)</strong>{" "}
              <Marker
                coordY={49.27361}
                coordX={-123.09806}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              – At <em>1150 Station St, Vancouver, BC</em>. Convenient for those
              arriving by train (Amtrak or VIA Rail) or intercity bus. If you’re
              continuing to destinations like the Fraser Valley or heading east
              on Highway 1, this location simplifies your travel. Check operating
              hours, as some rental desks may not open late.
            </p>

            <h3>5. North Vancouver (Marine Drive)</h3>
            <p
              ref={(el) => setSpotRef("north-vancouver-rental", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Enterprise North Vancouver</strong>{" "}
              <Marker
                coordY={49.31970}
                coordX={-123.10300}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              – At <em>751 Marine Drive, North Vancouver, BC</em>. Ideal for
              North Shore residents or those planning trips toward Whistler.
              Renting here can save time and avoid downtown traffic or bridge
              congestion. Economy cars start around CA$40/day, SUVs a bit more if
              you’re heading for mountain adventures.
            </p>

            {/* SECTION: Local Regulations & Best Practices */}
            <h2 id="local-regulations">Local Regulations & Best Practices</h2>
            <p>
              In British Columbia, you must have a valid driver’s license (IDP
              recommended if yours isn’t in English). Minimum rental age is
              typically 21, with surcharges for drivers under 25. All BC
              vehicles must have liability insurance; check if your credit card
              or personal auto policy covers rental damages. Always follow the
              posted speed limits (in km/h), wear seat belts, and never drive
              under the influence—BC imposes tough impaired driving penalties.
              Beware of high parking costs downtown and abide by local signage.
              If driving outside the city in winter, note the requirement for
              winter-ready tires (M+S or snowflake) on many highways between
              October and March.
            </p>

            {/* SECTION: FAQ */}
            <h2 id="faq">FAQ</h2>
            <div className="faq-section">
              <h3>1. Do I really need a car if I’m staying downtown?</h3>
              <p>
                If your plans are confined to the city center, you can rely on
                public transit and walking. Parking fees downtown are high, and
                traffic can be congested. However, a rental car is great for day
                trips to Whistler, the North Shore, or other sites not easily
                accessible by bus or SkyTrain.
              </p>

              <h3>2. Is cross-border travel into the US allowed?</h3>
              <p>
                Yes, major rental companies typically let you drive into the US,
                but you must inform them in advance and check your travel
                documents. One-way drop-offs across the border may incur extra
                fees or may not be permitted by all agencies.
              </p>

              <h3>3. What about winter driving conditions?</h3>
              <p>
                Vancouver itself doesn’t get heavy snow often, but if you’re
                driving to Whistler or interior BC during winter, ensure your
                rental has M+S or winter tires. Check road conditions on DriveBC
                and always follow posted requirements for your route.
              </p>
            </div>

            {/* SECTION: Conclusion */}
            <h2 id="conclusion">Conclusion</h2>
            <p>
              Renting a car in Vancouver is the key to exploring British
              Columbia’s diverse landscapes beyond the city core. With a bit of
              planning—choosing the right vehicle type, location, and insurance
              coverage—you can enjoy a smooth rental experience. Keep an eye on
              local regulations and parking constraints, and you’ll be all set to
              discover everything from vibrant downtown neighborhoods to scenic
              mountain highways. Safe travels!
            </p>

            {/* BOTTOM COMPONENT: references, author, related articles, etc. */}
            <Bottom referencesData={referencesData} ref_data={ref_data} />
          </article>

          {/* RIGHT SIDE COLUMN */}
          <Right currentArticle={ref_data} />
        </div>
      </main>

      {/* MOBILE MAP OVERLAY */}
      <MobileMapOverlay
        ref={mobileMapRef}
        markers={locations}
        spotRefs={spotRefs}
      />

      <Footer />
    </>
  );
}